<template>
  <div>
    <b-container fluid class="pb-4 px-lg-4">
      <div class="sr-header">
        <h3 class="sr-title">Requisitos de tu firma electrónica</h3>
        <p class="sr-subtitle">{{ target ? target.label : "Firma electrónica" }}</p>
      </div>

      <p v-if="loading" class="sr-muted">Cargando…</p>

      <div v-else-if="failed" class="sr-card">
        <p class="sr-error">{{ failed }}</p>
        <button type="button" class="sr-btn sr-btn--ghost" @click="load">Reintentar</button>
      </div>

      <div v-else-if="done" class="sr-card sr-card--center">
        <i class="el-icon-circle-check sr-done-icon"></i>
        <h4 class="sr-card-title">Documentos enviados</h4>
        <p class="sr-muted">{{ done }}</p>
        <router-link :to="{ name: 'plans.additionals' }" class="sr-btn">Volver a adicionales</router-link>
      </div>

      <div v-else-if="!target" class="sr-card">
        <ul v-if="overview.requests.length" class="sr-status-list">
          <li v-for="r in overview.requests" :key="r.id">
            <i :class="r.status === 'approved' ? 'el-icon-circle-check sr-ok' : 'el-icon-time sr-wait'"></i>
            <span><strong>{{ r.label }}</strong>{{ statusLabel[r.status] }}</span>
          </li>
        </ul>
        <p v-else class="sr-muted">
          No tienes firmas electrónicas pendientes de documentos. Puedes comprar una en
          <router-link :to="{ name: 'plans.additionals' }">Adicionales</router-link>.
        </p>
      </div>

      <div v-else class="sr-steps">
        <p v-if="target.rejected !== undefined" class="sr-alert">
          <strong>Necesitamos que vuelvas a enviar tus documentos.</strong>
          {{ target.rejected || "" }}
        </p>

        <div class="sr-card">
          <h4 class="sr-card-title">1. ¿Para quién es la firma?</h4>
          <div class="sr-options" role="radiogroup">
            <button
              v-for="o in personOptions"
              :key="o.value"
              type="button"
              role="radio"
              :aria-checked="person === o.value"
              class="sr-option"
              :class="{ 'sr-option--on': person === o.value }"
              @click="person = o.value"
            >
              <i :class="o.icon" class="sr-option-icon"></i>
              <span><strong>{{ o.title }}</strong>{{ o.text }}</span>
            </button>
          </div>
        </div>

        <div v-if="person" class="sr-card">
          <h4 class="sr-card-title">2. Sube tus documentos</h4>
          <div class="sr-grid">
            <SignatureDropzone v-model="docs.ruc" input-id="doc-ruc" label="RUC" hint="Certificado del RUC del SRI" :exts="PDF" :max-mb="maxMb" />
            <SignatureDropzone v-model="docs.id_card" input-id="doc-cedula" label="Cédula frontal y posterior" hint="Hasta 2 archivos: frente y reverso" :exts="PDF_OR_IMAGE" :max-files="2" :max-mb="maxMb" />
            <SignatureDropzone v-model="docs.selfie" input-id="doc-selfie" label="Selfie sosteniendo tu cédula" hint="Tu rostro y la cédula deben verse claros" :exts="IMAGE" :max-mb="maxMb" />
            <SignatureDropzone v-if="company" v-model="docs.constitution" input-id="doc-constitucion" label="Constitución de la empresa" hint="Escritura de constitución" :exts="PDF" :max-mb="maxMb" />
            <SignatureDropzone v-if="company" v-model="docs.appointment" input-id="doc-nombramiento" label="Nombramiento del representante" hint="Nombramiento vigente e inscrito" :exts="PDF" :max-mb="maxMb" />
          </div>
          <p v-if="submitError" class="sr-error mt-3">{{ submitError }}</p>
          <div class="sr-footer">
            <span class="sr-muted"><i class="el-icon-lock"></i> Tus documentos solo los ve el equipo de Mydessk para emitir tu firma.</span>
            <button type="button" class="sr-btn" :disabled="!ready || busy" @click="submit">
              <i v-if="busy" class="el-icon-loading"></i> {{ busy ? "Enviando…" : "Enviar documentos" }}
            </button>
          </div>
        </div>
      </div>
    </b-container>
  </div>
</template>

<script>
import SignatureDropzone from "../components/SignatureDropzone";
import signatureRequestService, {
  SIGNATURE_MAX_MB,
  SIGNATURE_STATUS_LABEL,
  signaturesToComplete
} from "../services/signatureRequestService";

const errorText = (e, fallback) => {
  const data = e && e.response && e.response.data;
  const first = data && data.errors && Object.values(data.errors)[0];
  return (first && first[0]) || (data && data.message) || fallback;
};

/**
 * /dashboard/adicionales/firma: después de pagar la firma electrónica, el
 * cliente elige persona natural o representante de empresa y sube sus
 * documentos. ?compra=additional-12 o plan-34 elige la compra si hay varias.
 */
export default {
  name: "SignatureRequirementsPage",
  components: { SignatureDropzone },
  data() {
    return {
      PDF: ["pdf"],
      PDF_OR_IMAGE: ["pdf", "jpg", "jpeg", "png", "webp", "heic"],
      IMAGE: ["jpg", "jpeg", "png", "webp", "heic"],
      maxMb: SIGNATURE_MAX_MB,
      statusLabel: SIGNATURE_STATUS_LABEL,
      personOptions: [
        { value: "natural", title: "Persona natural", text: "La firma va a tu nombre.", icon: "el-icon-user" },
        { value: "company", title: "Representante de empresa", text: "Firmas en nombre de una compañía.", icon: "el-icon-office-building" }
      ],
      loading: true,
      failed: null,
      overview: { pending: [], requests: [] },
      person: null,
      docs: { ruc: [], id_card: [], selfie: [], constitution: [], appointment: [] },
      busy: false,
      submitError: null,
      done: null
    };
  },
  computed: {
    target() {
      const list = signaturesToComplete(this.overview);
      const wanted = this.$route.query.compra;
      return list.find(t => `${t.source_type}-${t.source_id}` === wanted) || list[0] || null;
    },
    company() {
      return this.person === "company";
    },
    ready() {
      const d = this.docs;
      return (
        !!this.person &&
        d.ruc.length === 1 &&
        d.id_card.length >= 1 &&
        d.selfie.length === 1 &&
        (!this.company || (d.constitution.length === 1 && d.appointment.length === 1))
      );
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.failed = null;
      try {
        const { data } = await signatureRequestService.getOverview();
        this.overview = (data && data.data) || { pending: [], requests: [] };
      } catch (e) {
        this.failed = errorText(e, "No pudimos cargar tu firma electrónica.");
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      if (!this.ready || !this.target) return;
      this.busy = true;
      this.submitError = null;
      try {
        const { data } = await signatureRequestService.submit({
          sourceType: this.target.source_type,
          sourceId: this.target.source_id,
          personType: this.person,
          docs: this.docs
        });
        this.done = (data && data.message) || "Recibimos tus documentos.";
      } catch (e) {
        this.submitError = errorText(e, "No pudimos guardar tus documentos. Intenta de nuevo.");
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3fdbbf;
$border: #e4eaf6;
$bg: #f7f8f9;

.sr-header {
  margin-bottom: 20px;
}
.sr-title {
  font-size: 20px;
  font-weight: 700;
  color: $primary;
  margin: 0;
}
.sr-subtitle,
.sr-muted {
  font-size: 13px;
  color: #909399;
  margin: 4px 0 0;
}
.sr-steps {
  max-width: 860px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.sr-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 12px;
  padding: 20px;
  max-width: 860px;
  &--center {
    text-align: center;
    max-width: 520px;
  }
}
.sr-card-title {
  font-size: 15px;
  font-weight: 700;
  color: $primary;
  margin: 0 0 14px;
}
.sr-done-icon {
  font-size: 40px;
  color: #22a06b;
}
.sr-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}
.sr-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  border: 2px solid $border;
  border-radius: 12px;
  background: #fff;
  padding: 12px 14px;
  transition: all 0.15s;
  span {
    font-size: 12px;
    color: #606266;
  }
  strong {
    display: block;
    font-size: 14px;
    color: $primary;
  }
  &:hover {
    border-color: rgba($accent, 0.6);
  }
  &--on {
    border-color: $accent;
    background: rgba($accent, 0.08);
    .sr-option-icon {
      background: $accent;
      color: #fff;
    }
  }
}
.sr-option-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: $bg;
  color: $primary;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}
.sr-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
}
.sr-footer {
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.sr-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: $primary;
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 11px 22px;
  font-size: 14px;
  font-weight: 700;
  margin-top: 4px;
  &:hover {
    color: #fff;
    text-decoration: none;
    opacity: 0.92;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &--ghost {
    background: #fff;
    color: $primary;
    border: 1px solid $border;
  }
}
.sr-error {
  font-size: 13px;
  font-weight: 600;
  color: #e5484d;
}
.sr-alert {
  background: #fdecec;
  color: #c4302b;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 13px;
  margin: 0;
}
.sr-status-list {
  list-style: none;
  padding: 0;
  margin: 0;
  li {
    display: flex;
    gap: 10px;
    padding: 6px 0;
    font-size: 13px;
    color: #606266;
  }
  strong {
    display: block;
    color: $primary;
  }
}
.sr-ok {
  color: #22a06b;
  margin-top: 2px;
}
.sr-wait {
  color: #e6a23c;
  margin-top: 2px;
}
@media (max-width: 575px) {
  .sr-footer .sr-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
